import "./skeleton.css";

/**
 * The shared loading skeletons.
 *
 * Replaces the spinner-in-a-card that every screen used to show while it
 * loaded. Owner: "ปรับ progressbar แบบนี้ในแอฟทั้งหมดให้เปลี่ยนมาใช้แบบ page
 * loading แบบ workmate/Front" - modelled on that repo's own page-loading
 * pattern, where a screen renders shimmering placeholders in the SHAPE of the
 * content that is coming (`SkeletonRows`, `SkeletonProfile`, the `.skeleton`
 * shimmer in its tokens.css) rather than a spinner floating in an empty page.
 *
 * Why that is better here and not just different: a spinner in a 440px card at
 * the top-left of an empty 1440px page says only "something is happening". A
 * skeleton says what is coming, keeps the page from jumping when the data
 * lands, and makes a slow screen feel like a screen rather than a gap. The
 * shimmer itself is the "still working" signal the spinner used to be.
 *
 * The wording each screen already had ("กำลังโหลด War Room...") is NOT thrown
 * away - it moves into `label`, is announced through role="status", and stays
 * available to a screen reader. It simply stops being the only thing a sighted
 * operator gets.
 *
 * Deliberately NOT used for: a button's own busy state, an image thumbnail, or
 * a video poster. A skeleton the size of a 24px control communicates nothing
 * that a spinner does not, so those keep the spinner they have.
 */

/** One shimmering placeholder. Width/height are the caller's, because a skeleton only helps if it is the size of the thing it stands in for. */
export function SkeletonBlock({ width, height = 12, radius = 6, className = "" }) {
  return (
    <span
      className={`wx-skel ${className}`}
      style={{ width, height, borderRadius: radius }}
      aria-hidden="true"
    />
  );
}

/**
 * Widths that vary a little from row to row. A column of identical bars reads
 * as a loading GRAPHIC; slightly ragged ones read as text that has not arrived
 * yet, which is what this is standing in for.
 */
function raggedWidth(index, base, spread = 3) {
  return `${base + ((index * 37) % spread) * 18}px`;
}

/** The page heading + toolbar every list screen has above its content. */
function SkeletonPageHead() {
  return (
    <div className="wx-skel-head">
      <div className="wx-skel-head-text">
        <SkeletonBlock width="220px" height={22} radius={7} />
        <SkeletonBlock width="380px" height={12} />
      </div>
      <SkeletonBlock width="120px" height={30} radius={8} />
    </div>
  );
}

function SkeletonToolbar() {
  return (
    <div className="wx-skel-toolbar">
      <SkeletonBlock width="280px" height={34} radius={8} />
      <SkeletonBlock width="150px" height={34} radius={8} />
      <SkeletonBlock width="150px" height={34} radius={8} />
    </div>
  );
}

/** Rows, for the table screens - which is most of this product. */
function SkeletonTable({ rows = 8 }) {
  return (
    <div className="wx-skel-panel">
      <div className="wx-skel-panel-head">
        <SkeletonBlock width="160px" height={14} />
        <SkeletonBlock width="80px" height={12} />
      </div>
      <div className="wx-skel-rows">
        {Array.from({ length: rows }).map((_, index) => (
          <div className="wx-skel-row" key={index}>
            <SkeletonBlock width="34px" height={34} radius={9} />
            <div className="wx-skel-row-text">
              <SkeletonBlock width={raggedWidth(index, 150)} height={12} />
              <SkeletonBlock width={raggedWidth(index + 1, 90)} height={10} />
            </div>
            <SkeletonBlock className="wx-skel-hide-sm" width="140px" height={11} />
            <SkeletonBlock className="wx-skel-hide-sm" width="110px" height={11} />
            <SkeletonBlock width="72px" height={22} radius={999} />
          </div>
        ))}
      </div>
    </div>
  );
}

/** A grid of cards, for the screens that lay their records out as tiles. */
function SkeletonCards({ cards = 6 }) {
  return (
    <div className="wx-skel-cards">
      {Array.from({ length: cards }).map((_, index) => (
        <div className="wx-skel-card" key={index}>
          <div className="wx-skel-card-top">
            <SkeletonBlock width="40px" height={40} radius={10} />
            <SkeletonBlock width="64px" height={20} radius={999} />
          </div>
          <SkeletonBlock width={raggedWidth(index, 140)} height={14} />
          <SkeletonBlock width={raggedWidth(index + 2, 100)} height={11} />
          <div className="wx-skel-card-grid">
            <SkeletonBlock height={30} radius={8} />
            <SkeletonBlock height={30} radius={8} />
            <SkeletonBlock height={30} radius={8} />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Columns of cards - the War Room board and anything else laid out in lanes. */
function SkeletonBoard({ columns = 4 }) {
  return (
    <div className="wx-skel-board">
      {Array.from({ length: columns }).map((_, columnIndex) => (
        <div className="wx-skel-board-col" key={columnIndex}>
          <div className="wx-skel-board-head">
            <SkeletonBlock width="110px" height={13} />
            <SkeletonBlock width="28px" height={18} radius={6} />
          </div>
          {Array.from({ length: 3 + (columnIndex % 2) }).map((_, cardIndex) => (
            <div className="wx-skel-board-card" key={cardIndex}>
              <SkeletonBlock width={raggedWidth(cardIndex + columnIndex, 110)} height={12} />
              <SkeletonBlock width="70px" height={10} />
              <SkeletonBlock width="56px" height={18} radius={999} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/** A map screen: the map itself is the page, with a list beside it. */
function SkeletonMap() {
  return (
    <div className="wx-skel-map">
      <div className="wx-skel-map-canvas">
        <SkeletonBlock width="100%" height="100%" radius={12} />
      </div>
      <div className="wx-skel-panel wx-skel-map-side">
        <div className="wx-skel-panel-head">
          <SkeletonBlock width="130px" height={14} />
        </div>
        <div className="wx-skel-rows">
          {Array.from({ length: 5 }).map((_, index) => (
            <div className="wx-skel-row" key={index}>
              <SkeletonBlock width="28px" height={28} radius={999} />
              <div className="wx-skel-row-text">
                <SkeletonBlock width={raggedWidth(index, 120)} height={11} />
                <SkeletonBlock width="70px" height={10} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** A dashboard: a row of stat tiles over two panels. */
function SkeletonDashboard() {
  return (
    <>
      <div className="wx-skel-stats">
        {Array.from({ length: 4 }).map((_, index) => (
          <div className="wx-skel-stat" key={index}>
            <SkeletonBlock width="90px" height={11} />
            <SkeletonBlock width="120px" height={26} radius={8} />
            <SkeletonBlock width="70px" height={10} />
          </div>
        ))}
      </div>
      <div className="wx-skel-split">
        <div className="wx-skel-panel">
          <div className="wx-skel-panel-head">
            <SkeletonBlock width="150px" height={14} />
          </div>
          <div className="wx-skel-chart">
            <SkeletonBlock width="100%" height="100%" radius={10} />
          </div>
        </div>
        <div className="wx-skel-panel">
          <div className="wx-skel-panel-head">
            <SkeletonBlock width="120px" height={14} />
          </div>
          <div className="wx-skel-rows">
            {Array.from({ length: 4 }).map((_, index) => (
              <div className="wx-skel-row" key={index}>
                <SkeletonBlock width="30px" height={30} radius={999} />
                <div className="wx-skel-row-text">
                  <SkeletonBlock width={raggedWidth(index, 130)} height={11} />
                  <SkeletonBlock width="80px" height={10} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

/** A tree of nodes - the org chart screens. */
function SkeletonOrgChart() {
  return (
    <div className="wx-skel-org">
      <div className="wx-skel-org-row">
        <SkeletonBlock width="180px" height={64} radius={12} />
      </div>
      <div className="wx-skel-org-row">
        {Array.from({ length: 3 }).map((_, index) => (
          <SkeletonBlock key={index} width="160px" height={58} radius={12} />
        ))}
      </div>
      <div className="wx-skel-org-row">
        {Array.from({ length: 6 }).map((_, index) => (
          <SkeletonBlock key={index} width="120px" height={50} radius={10} />
        ))}
      </div>
    </div>
  );
}

const BODIES = {
  table: SkeletonTable,
  cards: SkeletonCards,
  board: SkeletonBoard,
  map: SkeletonMap,
  dashboard: SkeletonDashboard,
  orgChart: SkeletonOrgChart,
};

/**
 * A whole screen, still loading.
 *
 * `label` is the sentence that screen already showed ("กำลังโหลดรายการเคส
 * SOS..."). It is announced, not drawn - the shimmer is what a sighted operator
 * reads. Keeping it means a screen reader still hears which screen is loading,
 * which the previous spinner-plus-text did give and which a bare skeleton would
 * have silently dropped.
 *
 * `variant` should be the shape of what the screen is about to render, because
 * a skeleton that does not match its screen is just a different loading
 * graphic. Unknown values fall back to `table` rather than rendering nothing.
 */
export default function PageSkeleton({ label, variant = "table", head = true, toolbar = true, rows, columns, cards }) {
  const Body = BODIES[variant] ?? SkeletonTable;
  const bodyProps = {};
  if (rows !== undefined) {
    bodyProps.rows = rows;
  }
  if (columns !== undefined) {
    bodyProps.columns = columns;
  }
  if (cards !== undefined) {
    bodyProps.cards = cards;
  }

  return (
    <div className="wx-skel-page" role="status" aria-busy="true">
      <span className="wx-visually-hidden">{label ?? "กำลังโหลดข้อมูล..."}</span>
      {head && <SkeletonPageHead />}
      {toolbar && <SkeletonToolbar />}
      <Body {...bodyProps} />
    </div>
  );
}

/**
 * One rectangular media area still loading - a passport scan, a map tile
 * canvas, a lazily-loaded chart chunk.
 *
 * Its own export rather than a PageSkeleton variant because it is not a page:
 * it fills whatever box it is dropped into. Deliberately ONE block and nothing
 * else - a title-and-rows skeleton in front of an image would promise a shape
 * that is never going to arrive.
 */
export function MediaSkeleton({ label, height = 260, radius = 10 }) {
  return (
    <div className="wx-skel-media" role="status" aria-busy="true">
      <span className="wx-visually-hidden">{label ?? "กำลังโหลด..."}</span>
      <SkeletonBlock width="100%" height={height} radius={radius} />
    </div>
  );
}

/**
 * A message transcript still loading. Bubbles alternate sides and vary in
 * width, because that is what a conversation looks like and a column of
 * identical bars would not read as one.
 */
export function ThreadSkeleton({ label, messages = 5 }) {
  return (
    <div className="wx-skel-thread" role="status" aria-busy="true">
      <span className="wx-visually-hidden">{label ?? "กำลังโหลดบทสนทนา..."}</span>
      {Array.from({ length: messages }).map((_, index) => (
        <div className={`wx-skel-bubble ${index % 2 ? "wx-skel-bubble--out" : ""}`} key={index}>
          <SkeletonBlock width={`${45 + ((index * 23) % 4) * 10}%`} height={index % 3 === 0 ? 46 : 32} radius={12} />
          <SkeletonBlock width="64px" height={9} />
        </div>
      ))}
    </div>
  );
}

/**
 * The whole application, before the shell itself can render.
 *
 * Used only at boot, while runtime config is being fetched - the one moment
 * there is no AppShell to put a page skeleton inside. It draws the shell's own
 * furniture (rail, topbar, content) so the first paint is already the shape the
 * app is about to be, instead of a card floating on an empty page.
 */
export function AppShellSkeleton({ label }) {
  return (
    <div className="wx-skel-shell" role="status" aria-busy="true" aria-live="polite">
      <span className="wx-visually-hidden">{label ?? "กำลังเตรียมระบบ Workmate X"}</span>

      <div className="wx-skel-shell-rail">
        <div className="wx-skel-shell-brand">
          <SkeletonBlock width="32px" height={32} radius={9} />
          <SkeletonBlock width="90px" height={12} />
        </div>
        {Array.from({ length: 3 }).map((_, groupIndex) => (
          <div className="wx-skel-shell-group" key={groupIndex}>
            <SkeletonBlock width="70px" height={9} />
            {Array.from({ length: 4 }).map((_, itemIndex) => (
              <SkeletonBlock key={itemIndex} width={`${62 + ((itemIndex * 17) % 3) * 12}%`} height={13} />
            ))}
          </div>
        ))}
      </div>

      <div className="wx-skel-shell-main">
        <div className="wx-skel-shell-topbar">
          <SkeletonBlock width="200px" height={12} />
          <SkeletonBlock width="120px" height={22} radius={999} />
        </div>
        <div className="wx-skel-shell-body">
          <SkeletonPageHead />
          <SkeletonToolbar />
          <SkeletonTable rows={5} />
        </div>
      </div>
    </div>
  );
}

/**
 * A record drawer or dialog, still loading - the same idea one layer in.
 *
 * Narrower and denser than the page variants because that is what a drawer is:
 * a title, a couple of status pills, then stacked label/value sections.
 */
export function DetailSkeleton({ label, sections = 3 }) {
  return (
    <div className="wx-skel-detail" role="status" aria-busy="true">
      <span className="wx-visually-hidden">{label ?? "กำลังโหลดรายละเอียด..."}</span>

      <div className="wx-skel-detail-head">
        <SkeletonBlock width="90px" height={10} />
        <SkeletonBlock width="220px" height={20} radius={7} />
        <SkeletonBlock width="130px" height={11} />
        <div className="wx-skel-detail-pills">
          <SkeletonBlock width="72px" height={22} radius={999} />
          <SkeletonBlock width="90px" height={22} radius={999} />
        </div>
      </div>

      {Array.from({ length: sections }).map((_, sectionIndex) => (
        <div className="wx-skel-detail-sec" key={sectionIndex}>
          <SkeletonBlock width="120px" height={11} />
          {Array.from({ length: 3 }).map((_, rowIndex) => (
            <div className="wx-skel-detail-kv" key={rowIndex}>
              <SkeletonBlock width={raggedWidth(rowIndex, 80, 2)} height={11} />
              <SkeletonBlock width={raggedWidth(rowIndex + sectionIndex, 110)} height={11} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
